import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, money, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/DatePicker";
import { Switch } from "@/components/ui/switch";
import { GearSix, BellRinging, WarningCircle, Crown, CaretRight, EnvelopeSimple, Copy, Check, MinusCircle, Clock, CalendarPlus, Stack } from "@phosphor-icons/react";
import { CurrencySelect } from "@/components/CurrencySelect";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const cycles = [
  { value: "weekly", label: "Weekly" },
  { value: "bi-weekly", label: "Bi-weekly (every 2 weeks)" },
  { value: "semi-monthly", label: "Semi-monthly (1st–15th / 16th–end)" },
  { value: "monthly", label: "Monthly" },
];

const EARNING_TYPE_LABELS = [
  { key: "regular", label: "Regular Pay" },
  { key: "overtime", label: "Overtime" },
  { key: "paid_duty", label: "Paid Duty" },
  { key: "court", label: "Court Pay" },
  { key: "other", label: "Other Earnings" },
];
const DEFAULT_TIMING = { mode: "current", cycles: 1 };

export default function Settings() {
  const { user, setUser } = useAuth();
  const qc = useQueryClient();
  const [form, setForm] = useState({
    name: user?.name || "",
    base_hourly_rate: user?.base_hourly_rate ?? 0,
    pay_period_cycle: user?.pay_period_cycle || "bi-weekly",
    pay_period_anchor: user?.pay_period_anchor || "2025-01-06",
    employer: user?.employer || "",
    annual_salary: user?.annual_salary ?? 0,
    payroll_schedule: user?.payroll_schedule || "",
    standard_gross_pay: user?.standard_gross_pay ?? 0,
    standard_net_pay: user?.standard_net_pay ?? 0,
    default_paid_duty_rate: user?.default_paid_duty_rate ?? 0,
    default_court_rate: user?.default_court_rate ?? 0,
    default_overtime_multiplier: String(user?.default_overtime_multiplier ?? 1.5),
    regular_scheduled_hours: user?.regular_scheduled_hours ?? 0,
    currency: user?.currency || "CAD",
  });
  const [prefs, setPrefs] = useState({
    underpayment_alert: user?.notification_prefs?.underpayment_alert ?? true,
    payday_summary: user?.notification_prefs?.payday_summary ?? true,
  });
  const [timing, setTiming] = useState(() => {
    const base = {};
    EARNING_TYPE_LABELS.forEach(({ key }) => {
      base[key] = { ...DEFAULT_TIMING, ...(user?.payment_timing?.[key] || {}) };
    });
    return base;
  });
  const setTimingField = (key, patch) =>
    setTiming((t) => ({ ...t, [key]: { ...t[key], ...patch } }));
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyForwarding = async () => {
    try {
      await navigator.clipboard.writeText(user?.forwarding_address || "");
    } catch {
      const ta = document.createElement("textarea");
      ta.value = user?.forwarding_address || "";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    setCopied(true);
    toast.success("Forwarding address copied.");
    setTimeout(() => setCopied(false), 2000);
  };

  const applySalaryToRate = () => {
    const salary = parseFloat(form.annual_salary) || 0;
    if (salary <= 0) {
      toast.error("Enter an annual salary first.");
      return;
    }
    const rate = (salary / 2080).toFixed(2);
    setForm((f) => ({ ...f, base_hourly_rate: rate }));
    toast.success(`Base hourly rate set to $${rate}/hr (salary ÷ 2080 hrs).`);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const cleanTiming = {};
      for (const { key } of EARNING_TYPE_LABELS) {
        const cfg = timing[key];
        if (cfg.mode === "cycles") {
          const n = parseInt(cfg.cycles, 10);
          if (!Number.isInteger(n) || n < 1) {
            toast.error(`${EARNING_TYPE_LABELS.find((e) => e.key === key).label}: pay cycles must be a positive whole number.`);
            setSaving(false);
            return;
          }
          cleanTiming[key] = { mode: "cycles", cycles: n };
        } else {
          cleanTiming[key] = { mode: cfg.mode, cycles: 1 };
        }
      }
      const { data } = await api.put("/settings", {
        name: form.name,
        base_hourly_rate: parseFloat(form.base_hourly_rate) || 0,
        pay_period_cycle: form.pay_period_cycle,
        pay_period_anchor: form.pay_period_anchor,
        employer: form.employer,
        payroll_schedule: form.payroll_schedule,
        annual_salary: parseFloat(form.annual_salary) || 0,
        standard_gross_pay: parseFloat(form.standard_gross_pay) || 0,
        standard_net_pay: parseFloat(form.standard_net_pay) || 0,
        default_paid_duty_rate: parseFloat(form.default_paid_duty_rate) || 0,
        default_court_rate: parseFloat(form.default_court_rate) || 0,
        default_overtime_multiplier: parseFloat(form.default_overtime_multiplier) || 1.5,
        regular_scheduled_hours: parseFloat(form.regular_scheduled_hours) || 0,
        currency: form.currency,
        notification_prefs: prefs,
        payment_timing: cleanTiming,
      });
      setUser(data);
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["projections"] });
      qc.invalidateQueries({ queryKey: ["overtime"] });
      qc.invalidateQueries({ queryKey: ["regular-hours"] });
      toast.success("Settings saved.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setSaving(false);
  };

  return (
    <div className="animate-fade-up max-w-xl" data-testid="settings-page">
      <div className="flex items-center gap-3 mb-8">
        <div className="h-10 w-10 rounded-md bg-primary/15 flex items-center justify-center">
          <GearSix className="h-6 w-6 text-primary" weight="bold" />
        </div>
        <div>
          <p className="overline text-primary">Configuration</p>
          <h1 className="text-3xl font-black tracking-tight">Settings</h1>
        </div>
      </div>

      <Link to="/settings/billing" data-testid="billing-link" className="block mb-4">
        <Card className="p-4 bg-card border-border hover:border-primary/50 transition-colors duration-200">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-md bg-primary/15 flex items-center justify-center">
                <Crown className="h-5 w-5 text-primary" weight="fill" />
              </div>
              <div>
                <p className="font-semibold">Billing &amp; Plan</p>
                <p className="text-xs text-muted-foreground">
                  You're on the <span className={`font-semibold ${user?.plan === "pro" ? "text-primary" : ""}`}>
                    {user?.plan === "pro" ? "Pro" : "Free"}</span> plan · manage subscription
                </p>
              </div>
            </div>
            <CaretRight className="h-5 w-5 text-muted-foreground" weight="bold" />
          </div>
        </Card>
      </Link>

      <Link to="/deductions" data-testid="deductions-link" className="block mb-4">
        <Card className="p-4 bg-card border-border hover:border-primary/50 transition-colors duration-200">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-md bg-rose-500/15 flex items-center justify-center">
                <MinusCircle className="h-5 w-5 text-rose-400" weight="bold" />
              </div>
              <div>
                <p className="font-semibold">Deductions &amp; Earnings</p>
                <p className="text-xs text-muted-foreground">
                  Taxes, pension, benefits &amp; more · used in your net pay
                </p>
              </div>
            </div>
            <CaretRight className="h-5 w-5 text-muted-foreground" weight="bold" />
          </div>
        </Card>
      </Link>

      <Link to="/pay-types" data-testid="pay-types-link" className="block mb-4">
        <Card className="p-4 bg-card border-border hover:border-primary/50 transition-colors duration-200">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-md bg-sky-500/15 flex items-center justify-center">
                <Stack className="h-5 w-5 text-sky-400" weight="bold" />
              </div>
              <div>
                <p className="font-semibold">Pay Types</p>
                <p className="text-xs text-muted-foreground">Unlimited custom pay types · quick-start from your occupation</p>
              </div>
            </div>
            <CaretRight className="h-5 w-5 text-muted-foreground" weight="bold" />
          </div>
        </Card>
      </Link>

      <Link to="/pay-changes" data-testid="pay-changes-link" className="block mb-4">
        <Card className="p-4 bg-card border-border hover:border-primary/50 transition-colors duration-200">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-md bg-sky-500/15 flex items-center justify-center">
                <CalendarPlus className="h-5 w-5 text-sky-400" weight="bold" />
              </div>
              <div>
                <p className="font-semibold">Future Pay Changes</p>
                <p className="text-xs text-muted-foreground">
                  Schedule raises, acting pay &amp; deduction changes · applied to your projections
                </p>
              </div>
            </div>
            <CaretRight className="h-5 w-5 text-muted-foreground" weight="bold" />
          </div>
        </Card>
      </Link>

      <Card className="p-5 mb-4 bg-card border-border" data-testid="email-forwarding-section">
        <div className="flex items-center gap-2 mb-1">
          <EnvelopeSimple className="h-5 w-5 text-primary" weight="bold" />
          <p className="overline text-primary">Email Forwarding</p>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Forward paid duty emails to this address and we'll extract the details with AI and add the entry automatically.
        </p>
        <div className="flex items-center gap-2">
          <code className="flex-1 min-w-0 truncate px-3 py-2.5 rounded-md bg-secondary/60 border border-border text-sm font-mono"
            data-testid="forwarding-address">
            {user?.forwarding_address || "—"}
          </code>
          <Button type="button" variant="outline" onClick={copyForwarding} data-testid="copy-forwarding-button"
            className="shrink-0 rounded-md">
            {copied ? <Check className="h-4 w-4 text-emerald-400" weight="bold" /> : <Copy className="h-4 w-4" weight="bold" />}
            <span className="ml-1.5">{copied ? "Copied" : "Copy"}</span>
          </Button>
        </div>
      </Card>

      <Card className="p-6 bg-card border-border">
        <form onSubmit={save} className="space-y-5">
          <div>
            <Label>Display Name</Label>
            <Input data-testid="settings-name" className="mt-1.5 bg-secondary/50"
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <Label>Currency</Label>
            <CurrencySelect value={form.currency} onChange={(c) => setForm({ ...form, currency: c })} />
            <p className="text-xs text-muted-foreground mt-1.5">Used to display all amounts across the app. Changing it won't convert past entries.</p>
          </div>
          <div>
            <Label>Base Hourly Rate ({user?.currency || "CAD"})</Label>
            <Input type="number" step="0.01" min="0" data-testid="settings-rate" className="mt-1.5 bg-secondary/50"
              value={form.base_hourly_rate} onChange={(e) => setForm({ ...form, base_hourly_rate: e.target.value })} />
            <p className="text-xs text-muted-foreground mt-1.5">Used to calculate regular and overtime pay.</p>
          </div>
          <div>
            <Label>Pay Period Cycle</Label>
            <Select value={form.pay_period_cycle} onValueChange={(v) => setForm({ ...form, pay_period_cycle: v })}>
              <SelectTrigger data-testid="settings-cycle" className="mt-1.5 bg-secondary/50"><SelectValue /></SelectTrigger>
              <SelectContent>
                {cycles.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {(form.pay_period_cycle === "bi-weekly" || form.pay_period_cycle === "weekly") && (
            <div>
              <Label>Period Anchor Date</Label>
              <div className="mt-1.5">
                <DatePicker testId="settings-anchor" value={form.pay_period_anchor}
                  onChange={(d) => setForm({ ...form, pay_period_anchor: d })} />
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">A known start date of one of your pay periods.</p>
            </div>
          )}

          <div className="pt-2 border-t border-border" data-testid="compensation-profile">
            <p className="overline text-primary mb-1">Compensation Profile</p>
            <p className="text-xs text-muted-foreground mb-4">Your employer, salary and standard pay details.</p>

            <div className="space-y-4">
              <div>
                <Label>Employer</Label>
                <Input data-testid="settings-employer" className="mt-1.5 bg-secondary/50"
                  value={form.employer} onChange={(e) => setForm({ ...form, employer: e.target.value })}
                  placeholder="Metro Police Service" />
              </div>

              <div>
                <Label>Annual Salary ({user?.currency || "CAD"})</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input type="number" step="0.01" min="0" data-testid="settings-annual-salary" className="bg-secondary/50"
                    value={form.annual_salary} onChange={(e) => setForm({ ...form, annual_salary: e.target.value })}
                    placeholder="104000" />
                  <Button type="button" variant="outline" data-testid="apply-salary-to-rate"
                    onClick={applySalaryToRate} className="shrink-0 rounded-md whitespace-nowrap">
                    Set hourly rate
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">"Set hourly rate" fills the base rate as salary ÷ 2080 hrs (editable).</p>
              </div>

              <div>
                <Label>Payroll Schedule (note)</Label>
                <Input data-testid="settings-payroll-schedule" className="mt-1.5 bg-secondary/50"
                  value={form.payroll_schedule} onChange={(e) => setForm({ ...form, payroll_schedule: e.target.value })}
                  placeholder="Paid every 2nd Friday" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Standard Gross Pay</Label>
                  <Input type="number" step="0.01" min="0" data-testid="settings-standard-gross" className="mt-1.5 bg-secondary/50"
                    value={form.standard_gross_pay} onChange={(e) => setForm({ ...form, standard_gross_pay: e.target.value })}
                    placeholder="4000" />
                </div>
                <div>
                  <Label>Standard Net Pay</Label>
                  <Input type="number" step="0.01" min="0" data-testid="settings-standard-net" className="mt-1.5 bg-secondary/50"
                    value={form.standard_net_pay} onChange={(e) => setForm({ ...form, standard_net_pay: e.target.value })}
                    placeholder="2900" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">When set, standard gross is used as the baseline for paycheque underpayment checks (plus overtime &amp; paid duty).</p>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Default Paid Duty Rate</Label>
                  <Input type="number" step="0.01" min="0" data-testid="settings-default-pd-rate" className="mt-1.5 bg-secondary/50"
                    value={form.default_paid_duty_rate} onChange={(e) => setForm({ ...form, default_paid_duty_rate: e.target.value })}
                    placeholder="65" />
                </div>
                <div>
                  <Label>Default Court Time Rate</Label>
                  <Input type="number" step="0.01" min="0" data-testid="settings-default-court-rate" className="mt-1.5 bg-secondary/50"
                    value={form.default_court_rate} onChange={(e) => setForm({ ...form, default_court_rate: e.target.value })}
                    placeholder="75" />
                </div>
              </div>

              <div>
                <Label>Default Overtime Multiplier</Label>
                <Select value={form.default_overtime_multiplier} onValueChange={(v) => setForm({ ...form, default_overtime_multiplier: v })}>
                  <SelectTrigger data-testid="settings-default-multiplier" className="mt-1.5 bg-secondary/50"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1.5">1.5× (Time and a half)</SelectItem>
                    <SelectItem value="2">2× (Double time)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Regular Scheduled Hours (per pay period)</Label>
                <Input type="number" step="0.25" min="0" data-testid="settings-regular-scheduled-hours" className="mt-1.5 bg-secondary/50"
                  value={form.regular_scheduled_hours} onChange={(e) => setForm({ ...form, regular_scheduled_hours: e.target.value })}
                  placeholder="80" />
                <p className="text-xs text-muted-foreground mt-1">For hourly workers: base pay = hourly rate × these hours each period (used when no salary is set).</p>
              </div>
            </div>
          </div>

          {/* Payment Timing */}
          <div className="pt-2" data-testid="payment-timing-section">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="h-4 w-4 text-primary" weight="bold" />
              <p className="overline text-primary">Payment Timing</p>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              Choose when each earnings type actually lands on your paycheck. Estimated paychecks use this so pay shows up when your employer pays it — not just when the work happened.
            </p>
            <div className="space-y-3">
              {EARNING_TYPE_LABELS.map(({ key, label }) => (
                <div key={key} className="flex flex-wrap items-center gap-3 rounded-md bg-secondary/40 p-3" data-testid={`timing-row-${key}`}>
                  <span className="text-sm font-medium w-32 shrink-0">{label}</span>
                  <div className="flex-1 min-w-[160px]">
                    <Select value={timing[key].mode} onValueChange={(v) => setTimingField(key, { mode: v })}>
                      <SelectTrigger className="bg-secondary/60" data-testid={`timing-mode-${key}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="current">Current paycheck</SelectItem>
                        <SelectItem value="next">Next paycheck</SelectItem>
                        <SelectItem value="cycles">X pay cycles later</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {timing[key].mode === "cycles" && (
                    <Input type="number" min="1" step="1" data-testid={`timing-cycles-${key}`}
                      className="w-24 bg-secondary/60" placeholder="e.g. 2"
                      value={timing[key].cycles}
                      onChange={(e) => setTimingField(key, { cycles: e.target.value })} />
                  )}
                </div>
              ))}
            </div>
          </div>

          <Button type="submit" disabled={saving} data-testid="settings-save" className="rounded-md font-semibold">
            {saving ? "Saving…" : "Save Settings"}
          </Button>
        </form>
      </Card>

      <Card className="p-6 mt-4 bg-card border-border" data-testid="notification-preferences">
        <p className="overline text-primary mb-1">Notification Preferences</p>
        <p className="text-sm text-muted-foreground mb-5">Choose which emails DutyTrack sends to {user?.email}.</p>
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4 p-4 rounded-md bg-secondary/40">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-md bg-destructive/15 flex items-center justify-center shrink-0">
                <WarningCircle className="h-5 w-5 text-destructive" weight="bold" />
              </div>
              <div>
                <p className="text-sm font-semibold">Underpayment alerts</p>
                <p className="text-xs text-muted-foreground">Email me when a paycheque is flagged as underpaid, with the shortfall and unpaid entries.</p>
              </div>
            </div>
            <Switch
              data-testid="toggle-underpayment-alert"
              checked={prefs.underpayment_alert}
              onCheckedChange={(v) => setPrefs({ ...prefs, underpayment_alert: v })}
            />
          </div>
          <div className="flex items-center justify-between gap-4 p-4 rounded-md bg-secondary/40">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-md bg-primary/15 flex items-center justify-center shrink-0">
                <BellRinging className="h-5 w-5 text-primary" weight="bold" />
              </div>
              <div>
                <p className="text-sm font-semibold">Payday summary</p>
                <p className="text-xs text-muted-foreground">Email me a summary on every payday: gross, net, hours, and expected vs actual.</p>
              </div>
            </div>
            <Switch
              data-testid="toggle-payday-summary"
              checked={prefs.payday_summary}
              onCheckedChange={(v) => setPrefs({ ...prefs, payday_summary: v })}
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-4">Preferences are saved with the "Save Settings" button above.</p>
      </Card>

      <Card className="p-5 mt-4 bg-card border-border">
        <p className="overline text-muted-foreground mb-2">Overtime Reference</p>
        <p className="text-sm text-muted-foreground">
          At {money(parseFloat(form.base_hourly_rate) || 0, user?.currency)}base rate — 1.5× ={" "}
          <span className="text-foreground font-semibold">{money((parseFloat(form.base_hourly_rate) || 0) * 1.5, user?.currency)}/hr</span>,{" "}
          2× = <span className="text-foreground font-semibold">{money((parseFloat(form.base_hourly_rate) || 0) * 2, user?.currency)}/hr</span>.
        </p>
      </Card>
    </div>
  );
}
